import * as React from 'react';
import * as _ from 'lodash';
import {
  ControlState,
  ControlStyleClass,
  ControlValidation,
  ControlValidationField,
  ControlValidationSizeField,
  DateFormatEnum,
  DisplayData,
  formatDate,
  formatDateISO,
  jsJodaLocalDateToJavascriptDate,
  Logging,
  renderFieldValidation,
  renderValidationMessage,
  utcToLocal,
} from '@vcd-ecm-ui/Common';
import {
  VtxButton,
  VtxDayPicker,
  VtxInput,
  VtxRadio,
  VtxRadioGroup,
  VtxSelect,
  VtxOption,
} from '@vertexinc/vtx-ui-react-component-library';
import * as JSJoda from '@js-joda/core';
import SearchFilterProps, {
  FilterConditionEnum,
  FilterOptionEnum,
  getSearchFilterAsObject,
  getSearchFilterAsString,
  SearchFilterName,
  SearchFilterType,
} from './SearchFilterProps.interface';
import './search-filter.scss';
import { FilterTypeEnum } from '../..';

// Default Style
const defaultControlStyle: ControlStyleClass = {
  fieldHeader: 'defaultFieldHeader',
  fieldBase: 'defaultFieldBase',
  fieldEnabled: 'defaultFieldEnabled',
  fieldDisabled: 'defaultFieldDisabled',
  fieldValidation: 'searchFilterFieldValidation',
  fieldDropDown: null,
};

const controlState: ControlState = {
  editable: true,
  disabled: false,
  readOnly: false,
};

// Text Filter Options
export const TextFilterOptions: DisplayData[] = [
  {
    data: FilterOptionEnum.CONTAINS,
    display: 'Contains',
  },
  {
    data: FilterOptionEnum.NOT_CONTAINS,
    display: 'Not contains',
  },
  {
    data: FilterOptionEnum.EQUALS,
    display: 'Equals',
  },
  {
    data: FilterOptionEnum.NOT_EQUALS,
    display: 'Not equals',
  },
  {
    data: FilterOptionEnum.STARTS_WITH,
    display: 'Starts with',
  },
  {
    data: FilterOptionEnum.ENDS_WITH,
    display: 'Ends with',
  },
  {
    data: FilterOptionEnum.IS_EMPTY,
    display: 'Is empty',
  },
  {
    data: FilterOptionEnum.IS_NOT_EMPTY,
    display: 'Is not empty',
  },
  {
    data: FilterOptionEnum.IS_NULL,
    display: 'Is null',
  },
  {
    data: FilterOptionEnum.IS_NOT_NULL,
    display: 'Is not null',
  },
];

// Date Filter Options
export const DateFilterOptions: DisplayData[] = [
  {
    data: FilterOptionEnum.EQUALS,
    display: 'Equals',
  },
  {
    data: FilterOptionEnum.NOT_EQUALS,
    display: 'Not equals',
  },
  {
    data: FilterOptionEnum.LESS_THAN,
    display: 'Is before',
  },
  {
    data: FilterOptionEnum.LESS_THAN_OR_EQUALS,
    display: 'Is before or equals',
  },
  {
    data: FilterOptionEnum.GREATER_THAN,
    display: 'Is after',
  },
  {
    data: FilterOptionEnum.GREATER_THAN_OR_EQUALS,
    display: 'Is after or equals',
  },
  {
    data: FilterOptionEnum.IN_RANGE_INCLUDED,
    display: 'In range (included)',
  },
  {
    data: FilterOptionEnum.IN_RANGE_EXCLUDED,
    display: 'In range (excluded)',
  },
  {
    data: FilterOptionEnum.IS_NULL,
    display: 'Is null',
  },
  {
    data: FilterOptionEnum.IS_NOT_NULL,
    display: 'Is not null',
  },
];

// Number Filter Options
export const NumberFilterOptions: DisplayData[] = [
  {
    data: FilterOptionEnum.EQUALS,
    display: 'Equals',
  },
  {
    data: FilterOptionEnum.NOT_EQUALS,
    display: 'Not equals',
  },
  {
    data: FilterOptionEnum.LESS_THAN,
    display: 'Less than',
  },
  {
    data: FilterOptionEnum.LESS_THAN_OR_EQUALS,
    display: 'Less than or equals',
  },
  {
    data: FilterOptionEnum.GREATER_THAN,
    display: 'Greater than',
  },
  {
    data: FilterOptionEnum.GREATER_THAN_OR_EQUALS,
    display: 'Greater than or equals',
  },
  {
    data: FilterOptionEnum.IN_RANGE_INCLUDED,
    display: 'In range (included)',
  },
  {
    data: FilterOptionEnum.IN_RANGE_EXCLUDED,
    display: 'In range (excluded)',
  },
  {
    data: FilterOptionEnum.IS_NULL,
    display: 'Is null',
  },
  {
    data: FilterOptionEnum.IS_NOT_NULL,
    display: 'Is not null',
  },
];

// Decimal Filter Options
export const DecimalFilterOptions: DisplayData[] = [
  {
    data: FilterOptionEnum.EQUALS,
    display: 'Equals',
  },
  {
    data: FilterOptionEnum.NOT_EQUALS,
    display: 'Not equals',
  },
  {
    data: FilterOptionEnum.LESS_THAN,
    display: 'Less than',
  },
  {
    data: FilterOptionEnum.LESS_THAN_OR_EQUALS,
    display: 'Less than or equals',
  },
  {
    data: FilterOptionEnum.GREATER_THAN,
    display: 'Greater than',
  },
  {
    data: FilterOptionEnum.GREATER_THAN_OR_EQUALS,
    display: 'Greater than or equals',
  },
  {
    data: FilterOptionEnum.IN_RANGE_INCLUDED,
    display: 'In range (included)',
  },
  {
    data: FilterOptionEnum.IN_RANGE_EXCLUDED,
    display: 'In range (excluded)',
  },
  {
    data: FilterOptionEnum.IS_NULL,
    display: 'Is null',
  },
  {
    data: FilterOptionEnum.IS_NOT_NULL,
    display: 'Is not null',
  },
];

const SearchFilter = ({
  title,
  filterType,
  dataIndex,
  intputPrefix = null,
  includeNullOrEmpty = true,
  dateFormat = 'MM/DD/YYYY',
  isTimestamp = false,
  minimum = null,
  maximum = null,
  props,
}: SearchFilterProps): JSX.Element => {
  // Get the Filter Options for the Filter Type
  const getFilterOptions = (): DisplayData[] => {
    let filterOptions: DisplayData[] = [];

    switch (filterType) {
      case FilterTypeEnum.Text:
        filterOptions = TextFilterOptions;
        break;
      case FilterTypeEnum.Date:
        filterOptions = DateFilterOptions;
        break;
      case FilterTypeEnum.Number:
        filterOptions = NumberFilterOptions;
        break;
      case FilterTypeEnum.Decimal:
        filterOptions = DecimalFilterOptions;
        break;
      default:
        filterOptions = [];
        break;
    }

    // Remove the Null of Empty Filter Options if not needed
    if (!includeNullOrEmpty) {
      const nullOrEmptyOptions :string[] = [
        FilterOptionEnum.IS_NULL,
        FilterOptionEnum.IS_NOT_NULL,
        FilterOptionEnum.IS_EMPTY,
        FilterOptionEnum.IS_NOT_EMPTY,
      ];
      return filterOptions.filter((option: DisplayData) => !nullOrEmptyOptions.includes(option.data));
    }

    return filterOptions;
  };

  // Check if the Filter Option requires a Value
  const optionHasValue = (option: string) : boolean => {
    switch (option) {
      case FilterOptionEnum.IS_NULL:
      case FilterOptionEnum.IS_NOT_NULL:
      case FilterOptionEnum.IS_EMPTY:
      case FilterOptionEnum.IS_NOT_EMPTY:
        return false;
      default:
        return true;
    }
  };

  const textFilter: boolean = (filterType === FilterTypeEnum.Text);
  const dateFilter: boolean = (filterType === FilterTypeEnum.Date);
  const numberFilter: boolean = (filterType === FilterTypeEnum.Number);
  const decimalFilter: boolean = (filterType === FilterTypeEnum.Decimal);

  // Validate the Value for Maximum and Maximum
  const validateValue = (value: string) : boolean => {
    if (value === null) {
      return false;
    }

    if (numberFilter || decimalFilter) {
      if (value === '') {
        return false;
      }

      if ((minimum !== null) && (Number(value) < minimum)) {
        const error = `Value ${value} less than Minimum ${minimum}`;
        Logging.Info(error);
        return false;
      }

      if ((maximum !== null) && (Number(value) > maximum)) {
        const error = `Value ${value} greater than Maximum ${maximum}`;
        Logging.Info(error);
        return false;
      }
    }

    return true;
  };

  // Validate the Range
  const validateRange = (fromValue: string, toValue: string): boolean => {
    if (!validateValue(fromValue)) {
      return false;
    }

    if (!validateValue(toValue)) {
      return false;
    }

    if (numberFilter || decimalFilter) {
      if (Number(fromValue) >= Number(toValue)) {
        Logging.Info('Invalid Number Range:', fromValue, toValue);
        return false;
      }
    }

    if (dateFilter) {
      if (new Date(fromValue) >= new Date(toValue)) {
        Logging.Info('Invalid Date Range:', fromValue, toValue);
        return false;
      }
    }

    return true;
  };

  // Get Filter Options
  const filterOptions: DisplayData[] = getFilterOptions();

  const { selectedKeys } = props;

  Logging.Debug('SearchFilter:', dataIndex, selectedKeys);

  const [filter1Option, setFilter1Option] = React.useState(null);
  const [filter1FromValue, setFilter1FromValue] = React.useState(null);
  const [filter1ToValue, setFilter1ToValue] = React.useState(null);
  const [filterJoinCondition, setFilterJoinCondition] = React.useState(null);
  const [filter2Option, setFilter2Option] = React.useState(null);
  const [filter2FromValue, setFilter2FromValue] = React.useState(null);
  const [filter2ToValue, setFilter2ToValue] = React.useState(null);

  // Range selected
  const range1: boolean = (filter1Option?.toString().startsWith(FilterOptionEnum.IN_RANGE));
  const range2: boolean = (filter2Option?.toString().startsWith(FilterOptionEnum.IN_RANGE));

  // Only show the Condition if the Value 1 is filled in.  Check if Value 1 has a Value
  const showCondition = range1 ? (filter1FromValue && filter1ToValue) : filter1FromValue || !optionHasValue(filter1Option);

  // Filter 2 is only displayed if Filter 1 is configured
  const showFilter2 = showCondition && (filterJoinCondition !== FilterConditionEnum.NONE);

  // Validate the Values
  const validateValues = (): boolean => {
    const result = (!optionHasValue(filter1Option)
    || (range1 ? validateRange(filter1FromValue, filter1ToValue) : validateValue(filter1FromValue)))
    && (!optionHasValue(filter2Option)
    || (!showFilter2 || (range2 ? validateRange(filter2FromValue, filter2ToValue) : validateValue(filter2FromValue))));

    return result;
  };

  // Check if Reset Button is Enabled
  const clearButtonEnabled = true;

  // Search Button Pressed - Save the Search Filter as a stringified JSON Object
  const onSearch = (): void => {
    // Get the Search Filter as a String
    const searchFilterString: string = getSearchFilterAsString(
      filterType,
      filter1Option,
      filter1FromValue,
      range1 ? filter1ToValue : null,
      showCondition ? filterJoinCondition : null,
      showFilter2 ? filter2Option : null,
      showFilter2 ? filter2FromValue : null,
      showFilter2 ? range2 ? filter2ToValue : null : null,
      isTimestamp,
    );

    props.setSelectedKeys([searchFilterString]);
    Logging.Debug('SetSelectedKeys:', [searchFilterString]);

    props.confirm();
  };

  // Clear Button Pressed
  const onClear = (): void => {
    props.clearFilters();
  };

  // Join Condition Changed
  const onChangeJoinCondition = (condition: string): void => {
    Logging.Debug('onChangeJoinCondition', condition);
    setFilterJoinCondition(condition);
  };

  // Render the Options of the Select
  const renderSelectOptions = (options: DisplayData[]): JSX.Element[] => { // eslint-disable-line arrow-body-style
    return options.map((option: DisplayData) => {
      const { data, display } = option;
      return (
        <VtxOption disabled={false} key={data} value={data}>{display}</VtxOption>
      );
    });
  };

  // Date has Changed
  const onChangeDate = (
    id: string,
    newDate: JSJoda.LocalDate,
    onChangeValue: React.Dispatch<React.SetStateAction<string>>,
  ): void => {
    if (newDate) {
      // Convert from a JSJoda LocalDate to native Javascript Date
      const value: Date = jsJodaLocalDateToJavascriptDate(newDate);

      // Convert to a String Date (YYY-MM-DD)
      onChangeValue(formatDate(value, { dateFormat: DateFormatEnum.YMD, dateSeperator: '-' }));
    } else {
      onChangeValue(null);
    }
  };

  // Convert the Date Value to a Date Value if it is a String Value
  const convertToDateIfString = (date: Date): Date => {
    if (date) {
      if (typeof date === 'object') {
        return date;
      }

      return new Date(date);
    }

    return null;
  };

  // Remove Illegal Characters based on the Filter Type
  const removeIllegalCharacters = (inputValue: string): string => {
    if (inputValue) {
      switch (filterType) {
        case FilterTypeEnum.Decimal:
          // Allow Digits and . only
          return inputValue.replace(/[^0-9.]/g, '');
        case FilterTypeEnum.Number:
          // Allow Digits only
          return inputValue.replace(/[^0-9]/g, '');
        default:
          return inputValue;
      }
    }

    return inputValue;
  };

  // Render the Select
  const renderSelect = (
    id: string,
    value: string,
    onChange: React.Dispatch<React.SetStateAction<string>>,
    placeholder: string,
    controlValidation: ControlValidation,
  ): JSX.Element => {
    Logging.Debug('renderInput', id, value);

    return (
      <div className="fieldBase searchFilterType">
        <VtxSelect
          id={id}
          optionFilterProp="children"
          filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
          disabled={false}
          value={value === null ? undefined : value}
          size="small"
          onChange={(newValue: string): void => onChange(newValue)}
          placeholder={placeholder}
        >
          {renderSelectOptions(filterOptions)}
        </VtxSelect>
        {renderFieldValidation(id, value || '', controlState, controlValidation, defaultControlStyle)}
      </div>
    );
  };

  // Render the Input
  const renderInput = (
    id: string,
    value: string,
    onChange: React.Dispatch<React.SetStateAction<string>>,
    range: boolean,
    direction: string,
    controlValidation: ControlValidation,
  ): JSX.Element => {
    Logging.Debug('renderInput', id, value);
    const placeholder = textFilter ? 'Filter ...' : range ? `${direction} Value ...` : 'Value ...';

    return (
      <div className="fieldBase searchFilterValue">
        <VtxInput
          type="text"
          id={id}
          readOnly={false}
          disabled={false}
          placeholder={placeholder}
          value={value}
          onChange={(event): void => onChange(removeIllegalCharacters(event.target.value))}
          onPressEnter={() => onSearch}
          prefix={intputPrefix}
        />
        {renderFieldValidation(id, value, controlState, controlValidation, defaultControlStyle)}
      </div>
    );
  };

  // Render the Date
  const renderDate = (
    id: string,
    value: JSJoda.LocalDate,
    onChange: React.Dispatch<React.SetStateAction<string>>,
    range: boolean,
    direction: string,
    controlValidation: ControlValidation,
  ): JSX.Element => {
    const placeholder = range ? `Select ${direction} date` : 'Select date';

    return (
      <div className="fieldBase searchFilterValue">
        <VtxDayPicker
          id={id}
          disabled={false}
          onChange={(day): void => onChangeDate(id, day, onChange)}
          value={value}
          className="fullWidthVtxDayPicker"
          placeholder={placeholder}
          format={dateFormat}
        />
        {renderFieldValidation(id, value, controlState, controlValidation, defaultControlStyle)}
      </div>
    );
  };

  // Create a Default Search Filter
  const getDefaultSearchFilter = (): SearchFilterType => {
    const searchFilter: SearchFilterType = {
      name: SearchFilterName,
      version: 1,
      type: filterType,
      option1: filterOptions[0]?.data as FilterOptionEnum, // Use First Option
      valueFrom1: null,
      valueTo1: null,
      joinCondition: FilterConditionEnum.NONE,
      option2: filterOptions[0]?.data as FilterOptionEnum, // Use First Option
      valueFrom2: null,
      valueTo2: null,
      isTimestamp,
    };

    return searchFilter;
  };

  // Selected Keys has Changed - Update the Local State
  React.useEffect(() => {
    // If specified use the Search Filter Object, otherwise use Defaults
    const searchFilter: SearchFilterType = selectedKeys && selectedKeys[0]
      ? getSearchFilterAsObject(selectedKeys[0] as string)
      : getDefaultSearchFilter();

    setFilter1Option(searchFilter.option1);
    setFilter1FromValue(searchFilter.valueFrom1);
    setFilter1ToValue(searchFilter.valueTo1);

    setFilterJoinCondition(searchFilter.joinCondition);
    setFilter2Option(searchFilter.option2);
    setFilter2FromValue(searchFilter.valueFrom2);
    setFilter2ToValue(searchFilter.valueTo2);
  }, [selectedKeys]);

  // Convert the String Dates to a native Javascript Date to a JSJoda LocalDate
  const date1FromValue: JSJoda.LocalDate = dateFilter && filter1FromValue
    ? JSJoda.LocalDate.parse(formatDateISO(convertToDateIfString(filter1FromValue)))
    : null;

  const date1ToValue: JSJoda.LocalDate = dateFilter && filter1ToValue
    ? JSJoda.LocalDate.parse(formatDateISO(convertToDateIfString(filter1ToValue)))
    : null;

  const date2FromValue: JSJoda.LocalDate = dateFilter && filter2FromValue
    ? JSJoda.LocalDate.parse(formatDateISO(convertToDateIfString(filter2FromValue)))
    : null;

  const date2ToValue: JSJoda.LocalDate = dateFilter && filter2ToValue
    ? JSJoda.LocalDate.parse(formatDateISO(convertToDateIfString(filter2ToValue)))
    : null;

  const directionStart = textFilter || dateFilter ? 'From' : 'Minimum';
  const directionStop = textFilter || dateFilter ? 'To' : 'Maximum';

  const controlValidation: ControlValidation = {
    requiredFields: [
      `search-${dataIndex}-1-option`,
      `search-${dataIndex}-1-from-value`,
      `search-${dataIndex}-1-to-value`,
      `search-${dataIndex}-2-option`,
      `search-${dataIndex}-2-from-value`,
      `search-${dataIndex}-2-to-value`,
    ],
    minMaxValue: [],
    customMessages: [],
    hideEmptyValidation: true,
  };

  if (numberFilter || decimalFilter) {
    if ((minimum !== null) && (maximum !== null)) {
      controlValidation.minMaxValue.push({ field: `search-${dataIndex}-1-from-value`, minValue: minimum, maxValue: maximum });
      controlValidation.minMaxValue.push({ field: `search-${dataIndex}-1-to-value`, minValue: minimum, maxValue: maximum });
      controlValidation.minMaxValue.push({ field: `search-${dataIndex}-2-from-value`, minValue: minimum, maxValue: maximum });
      controlValidation.minMaxValue.push({ field: `search-${dataIndex}-2-to-value`, minValue: minimum, maxValue: maximum });
    } else if (minimum !== null) {
      controlValidation.minMaxValue.push({ field: `search-${dataIndex}-1-from-value`, minValue: minimum });
      controlValidation.minMaxValue.push({ field: `search-${dataIndex}-1-to-value`, minValue: minimum });
      controlValidation.minMaxValue.push({ field: `search-${dataIndex}-2-from-value`, minValue: minimum });
      controlValidation.minMaxValue.push({ field: `search-${dataIndex}-2-to-value`, minValue: minimum });
    } else if (maximum !== null) {
      controlValidation.minMaxValue.push({ field: `search-${dataIndex}-1-from-value`, maxValue: maximum });
      controlValidation.minMaxValue.push({ field: `search-${dataIndex}-1-to-value`, maxValue: maximum });
      controlValidation.minMaxValue.push({ field: `search-${dataIndex}-2-from-value`, maxValue: maximum });
      controlValidation.minMaxValue.push({ field: `search-${dataIndex}-2-to-value`, maxValue: maximum });
    }

    if (range1 && (filter1FromValue !== null) && (filter1ToValue !== null)) {
      if (Number(filter1FromValue) >= Number(filter1ToValue)) {
        const message = '\'Maximum\' must be greater than \'Minimum\'';
        controlValidation.customMessages.push({ field: `search-${dataIndex}-1-to-value`, message, type: 'Error' });
      }
    }

    if (range2 && (filter2FromValue !== null) && (filter2ToValue !== null)) {
      if (Number(filter2FromValue) >= Number(filter2ToValue)) {
        const message = '\'Maximum\' must be greater than \'Minimum\'';
        controlValidation.customMessages.push({ field: `search-${dataIndex}-2-to-value`, message, type: 'Error' });
      }
    }
  }

  if (dateFilter) {
    if (range1 && (filter1FromValue !== null) && (filter1ToValue !== null)) {
      if (new Date(filter1FromValue) >= new Date(filter1ToValue)) {
        const message = '\'To\' date must be after \'From\' date';
        controlValidation.customMessages.push({ field: `search-${dataIndex}-1-to-value`, message, type: 'Error' });
      }
    }

    if (range2 && (filter2FromValue !== null) && (filter2ToValue !== null)) {
      if (new Date(filter2FromValue) >= new Date(filter2ToValue)) {
        const message = '\'To\' date must be after \'From\' date';
        controlValidation.customMessages.push({ field: `search-${dataIndex}-2-to-value`, message, type: 'Error' });
      }
    }
  }

  // Check if Search Button is Enabled
  const searchButtonEnabled = validateValues();

  return (
    <div className="searchFilter">
      {
        title
          && (
            <div className="fieldBase searchFilterTitle">
              {title} Filter
            </div>
          )
      }
      {renderSelect(`search-${dataIndex}-1-option`, filter1Option, setFilter1Option, null, controlValidation)}
      {
        optionHasValue(filter1Option)
          && (
            <>
              {
                dateFilter
                  ? (
                    <>
                      {renderDate(`search-${dataIndex}-1-from-value`, date1FromValue, setFilter1FromValue, range1, directionStart, controlValidation)}
                      {
                        range1
                          && (
                            renderDate(`search-${dataIndex}-1-to-value`, date1ToValue, setFilter1ToValue, range1, directionStop, controlValidation)
                          )
                      }
                    </>
                  )
                  : (
                    <>
                      {renderInput(`search-${dataIndex}-1-from-value`, filter1FromValue, setFilter1FromValue,
                        range1, directionStart, controlValidation)}
                      {
                        range1
                          && (
                            renderInput(`search-${dataIndex}-1-to-value`, filter1ToValue, setFilter1ToValue,
                              range1, directionStop, controlValidation)
                          )
                      }
                    </>
                  )
              }
            </>
          )
      }
      {
        showCondition
        && (
          <div className="searchFilterJoinCondition">
            <VtxRadioGroup
              defaultValue={FilterConditionEnum.NONE}
              value={filterJoinCondition}
              onChange={(event): void => onChangeJoinCondition(event.target.value)}
              direction="horizontal"
              name="filterJoinConditionGroup"
            >
              <VtxRadio value={FilterConditionEnum.NONE}>None</VtxRadio>
              <VtxRadio value={FilterConditionEnum.AND}>AND</VtxRadio>
              <VtxRadio value={FilterConditionEnum.OR}>OR</VtxRadio>
            </VtxRadioGroup>
          </div>
        )
      }
      {
        showFilter2
        && (
          <>
            {renderSelect(`search-${dataIndex}-2-option`, filter2Option, setFilter2Option, null, controlValidation)}
            {
              optionHasValue(filter2Option)
                && (
                  <>
                    {
                      dateFilter
                        ? (
                          <>
                            {renderDate(`search-${dataIndex}-2-from-value`, date2FromValue, setFilter2FromValue, range2,
                              directionStart, controlValidation)}
                            {
                              range2
                                && (
                                  renderDate(`search-${dataIndex}-2-to-value`, date2ToValue, setFilter2ToValue, range2,
                                    directionStop, controlValidation)
                                )
                            }
                          </>
                        )
                        : (
                          <>
                            {renderInput(`search-${dataIndex}-2-from-value`, filter2FromValue, setFilter2FromValue, range2,
                              directionStart, controlValidation)}
                            {
                              range2
                                && (
                                  renderInput(`search-${dataIndex}-2-to-value`, filter2ToValue, setFilter2ToValue, range2,
                                    directionStop, controlValidation)
                                )
                            }
                          </>
                        )
                    }
                  </>
                )
            }
          </>
        )
      }
      <div className="searchLine" />
      <div className="filterButtons">
        <div className="filterButtonBase">
          <VtxButton
            id="searchButton"
            onClick={onSearch}
            type="primary"
            disabled={!searchButtonEnabled}
          >
            Filter
          </VtxButton>
        </div>
        <div className="filterButtonBase">
          <VtxButton
            id="clearButton"
            onClick={onClear}
            type="default"
            disabled={!clearButtonEnabled}
          >
            Clear
          </VtxButton>
        </div>
      </div>
    </div>
  );
};

export default SearchFilter;
